import {
  useEffect, useMemo, useRef, useState,
} from 'react'
import {
  useSearchParams,
} from 'react-router-dom'
import {
  HotelBookingModal,
} from './HotelBookingModal'
import {
  cancelBookingApiV1HotelsCancelBookingPkPost,
  completeBookingApiV1HotelsCompleteBookingPost,
  getRateInfoApiV1HotelsRateInfoPost,
} from '../client'
import type {
  BookingResponse,
  RateInfoResponse,
} from '../client'
import {
  API_BASE_URL,
} from '../config'
import {
  useAuth,
} from '../hooks/useAuth'

// Default test target (Quarter Hualamphong, Bangkok). Cheapest refundable
// hotel available in our manual testing — used so internal staff can test
// the full flow on a tiny refundable rate.
const DEFAULT_HOTEL_ID = 'lp6839a'
const DEFAULT_HOTEL_NAME = 'The Quarter Hualamphong by UHG'

function todayPlus(days: number): string {
  const d = new Date()
  d.setDate(d.getDate() + days)
  return d.toISOString().slice(0, 10)
}

// FastAPI puts a human-readable string in `detail` for expected failures;
// validation errors put a list there. Fall back to the raw body so the
// "Technical details" disclosure stays useful.
function errorDetail(error: unknown): string {
  if (error && typeof error === 'object' && 'detail' in error) {
    const detail = (error as { detail?: unknown }).detail
    if (typeof detail === 'string') return detail
  }
  return JSON.stringify(error)
}

export function BookHotelPage(): JSX.Element {
  const { user } = useAuth()
  const [searchParams] = useSearchParams()
  const isPostStripeRedirect = searchParams.get('redirect_status') === 'succeeded'

  // Real-money page — gate to admins so a non-admin authenticated user
  // can't accidentally trigger a live charge to LiteAPI/Nuitée's Stripe.
  // The schedule_assistant book_hotel agent toolset (deferred follow-up
  // PR) will route end users through approval-gated agent tools instead
  // of this page directly.
  if (!user?.is_admin) {
    return (
      <div className="mx-auto max-w-md px-4 py-16 text-center">
        <h1 className="text-2xl font-semibold text-gray-900">Not available yet</h1>
        <p className="mt-2 text-sm text-gray-600">
          Hotel booking is in early access for the Catch team.
        </p>
      </div>
    )
  }

  // Form state
  const [hotelId, setHotelId] = useState(searchParams.get('hotel_id') ?? DEFAULT_HOTEL_ID)
  const [hotelName, setHotelName] = useState(
    searchParams.get('hotel_name') ?? DEFAULT_HOTEL_NAME,
  )
  const [checkin, setCheckin] = useState(searchParams.get('checkin') ?? todayPlus(17))
  const [checkout, setCheckout] = useState(searchParams.get('checkout') ?? todayPlus(18))
  const [adults, setAdults] = useState(parseInt(searchParams.get('adults') ?? '1', 10))
  const [guestFirstName, setGuestFirstName] = useState(searchParams.get('guest_first_name') ?? 'Yoav')
  const [guestLastName, setGuestLastName] = useState(searchParams.get('guest_last_name') ?? 'Ramon')
  const [guestEmail, setGuestEmail] = useState(searchParams.get('guest_email') ?? 'yoav@catchagent.ai')

  // Rate / modal
  const [rate, setRate] = useState<RateInfoResponse | null>(null)
  const [rateError, setRateError] = useState<string | null>(null)
  const [rateLoading, setRateLoading] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)

  // Post-Stripe-redirect state
  const [completeLoading, setCompleteLoading] = useState(false)
  const [completeResult, setCompleteResult] = useState<BookingResponse | null>(null)
  const [completeError, setCompleteError] = useState<string | null>(null)
  // Guard against re-firing /complete-booking on re-renders or StrictMode
  // double-mounts. The Stripe auth has already been captured by LiteAPI on
  // the first call — a second call would attempt to re-redeem an already-used
  // prebook (4012) and write a spurious PREBOOK_FAILED row.
  const completeFiredRef = useRef(false)

  const bookingContext = useMemo(
    () => ({
      hotel_id: hotelId,
      hotel_name: hotelName,
      checkin,
      checkout,
      adults,
      children: 0,
      guest_first_name: guestFirstName,
      guest_last_name: guestLastName,
      guest_email: guestEmail,
    }),
    [hotelId, hotelName, checkin, checkout, adults, guestFirstName, guestLastName, guestEmail],
  )

  const loadRate = async () => {
    setRateLoading(true)
    setRateError(null)
    setRate(null)
    try {
      const { data, error } = await getRateInfoApiV1HotelsRateInfoPost({
        body: {
          hotel_id: hotelId,
          checkin,
          checkout,
          adults,
          children: 0,
        },
        throwOnError: false,
      })
      if (error || !data) throw new Error(errorDetail(error))
      setRate(data)
    } catch (e) {
      setRateError(e instanceof Error ? e.message : 'Failed to load rate')
    } finally {
      setRateLoading(false)
    }
  }

  // After Stripe redirect, call /complete-booking server-side using the params
  // round-tripped through return_url. This is the moment when /rates/book is
  // executed; prebook age at this point should be ~5–10s.
  useEffect(() => {
    if (!isPostStripeRedirect) return
    if (completeFiredRef.current) return
    completeFiredRef.current = true

    const prebookId = searchParams.get('prebook_id')
    const transactionId = searchParams.get('transaction_id')
    const paymentIntent = searchParams.get('payment_intent')
    if (!prebookId || !transactionId) {
      setCompleteError('Missing booking reference in URL')
      return
    }
    setCompleteLoading(true)
    void completeBookingApiV1HotelsCompleteBookingPost({
      body: {
        prebook_id: prebookId,
        transaction_id: transactionId,
        hotel_id: searchParams.get('hotel_id') ?? '',
        hotel_name: searchParams.get('hotel_name') ?? '',
        checkin: searchParams.get('checkin') ?? '',
        checkout: searchParams.get('checkout') ?? '',
        adults: parseInt(searchParams.get('adults') ?? '1', 10),
        children: parseInt(searchParams.get('children') ?? '0', 10),
        price: parseFloat(searchParams.get('price') ?? '0'),
        currency: searchParams.get('currency') ?? 'USD',
        guest: {
          first_name: searchParams.get('guest_first_name') ?? '',
          last_name: searchParams.get('guest_last_name') ?? '',
          email: searchParams.get('guest_email') ?? '',
        },
        stripe_payment_intent_id: paymentIntent,
      },
      throwOnError: false,
    })
      .then(({ data, error }) => {
        if (error || !data) throw new Error(errorDetail(error))
        setCompleteResult(data)
      })
      .catch((e: unknown) => setCompleteError(e instanceof Error ? e.message : 'Unknown error'))
      .finally(() => setCompleteLoading(false))
  }, [isPostStripeRedirect, searchParams])

  const cancelBooking = async () => {
    if (!completeResult) return
    setCompleteLoading(true)
    try {
      const { data, error } = await cancelBookingApiV1HotelsCancelBookingPkPost({
        path: { booking_pk: completeResult.id },
        throwOnError: false,
      })
      if (error || !data) throw new Error(errorDetail(error))
      setCompleteResult(data)
    } catch (e) {
      setCompleteError(e instanceof Error ? e.message : 'Cancel failed')
    } finally {
      setCompleteLoading(false)
    }
  }

  // --- Render the post-Stripe-redirect result page ---
  if (isPostStripeRedirect) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-8">
        <h1 className="text-2xl font-semibold text-gray-900">Confirming your booking</h1>
        {completeLoading && (
          <p className="mt-4 text-gray-600">
            Locking in your reservation with the hotel…
          </p>
        )}
        {completeError && (
          <div className="mt-4 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-800">
            <p className="font-medium">We couldn't confirm this reservation.</p>
            <p className="mt-2 text-xs text-red-700">
              Your card was authorized but the room wasn't available by the time
              we tried to book it. The hold on your card will release within a few
              days; contact support if you need help sooner.
            </p>
            <details className="mt-2 text-xs">
              <summary className="cursor-pointer text-red-700">Technical details</summary>
              <p className="mt-1 whitespace-pre-wrap break-words text-red-600">
                {completeError}
              </p>
            </details>
          </div>
        )}
        {completeResult && (
          <div className="mt-4 space-y-3">
            <div
              className={
                'rounded border p-3 text-sm ' +
                (completeResult.status === 'cancelled'
                  ? 'border-gray-200 bg-gray-50 text-gray-800'
                  : 'border-green-200 bg-green-50 text-green-900')
              }
            >
              <p className="font-medium">
                {completeResult.status === 'cancelled'
                  ? 'Booking cancelled'
                  : 'Booking confirmed'}
              </p>
              {completeResult.hotel_name && (
                <p className="mt-1">
                  {completeResult.hotel_name} · {completeResult.checkin} → {completeResult.checkout}
                </p>
              )}
              <p className="mt-1 font-mono text-xs">
                bookingId: {completeResult.booking_id ?? '(none)'}
              </p>
              {completeResult.price !== null && (
                <p className="mt-1">
                  ${completeResult.price.toFixed(2)} {completeResult.currency} ·
                  payment: {completeResult.payment_status ?? '—'}
                </p>
              )}
              {completeResult.refund_amount != null && (
                <p className="mt-1">
                  Refund: ${completeResult.refund_amount.toFixed(2)} {completeResult.currency}
                  {completeResult.cancellation_fee != null && (
                    <> (fee: ${completeResult.cancellation_fee.toFixed(2)})</>
                  )}
                </p>
              )}
            </div>
            {completeResult.status === 'confirmed' && (
              <button
                type="button"
                onClick={cancelBooking}
                disabled={completeLoading}
                className="rounded border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                {completeLoading ? 'Cancelling…' : 'Cancel booking'}
              </button>
            )}
          </div>
        )}
      </div>
    )
  }

  // --- Render the form / book-hotel page ---
  return (
    <div className="mx-auto max-w-2xl px-4 py-8">
      <h1 className="text-2xl font-semibold text-gray-900">Book a hotel</h1>
      <p className="mt-1 text-sm text-gray-600">
        Quick test page to validate the LiteAPI deferred-mode booking flow end-to-end.
      </p>

      <div className="mt-6 grid grid-cols-1 gap-3 md:grid-cols-2">
        <label className="block text-sm">
          <span className="text-gray-600">Hotel ID</span>
          <input
            type="text"
            value={hotelId}
            onChange={(e) => setHotelId(e.target.value)}
            className="mt-1 w-full rounded border border-gray-300 px-3 py-2 font-mono text-sm"
          />
        </label>
        <label className="block text-sm">
          <span className="text-gray-600">Hotel name</span>
          <input
            type="text"
            value={hotelName}
            onChange={(e) => setHotelName(e.target.value)}
            className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm"
          />
        </label>
        <label className="block text-sm">
          <span className="text-gray-600">Check-in</span>
          <input
            type="date"
            value={checkin}
            onChange={(e) => setCheckin(e.target.value)}
            className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm"
          />
        </label>
        <label className="block text-sm">
          <span className="text-gray-600">Check-out</span>
          <input
            type="date"
            value={checkout}
            onChange={(e) => setCheckout(e.target.value)}
            className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm"
          />
        </label>
        <label className="block text-sm">
          <span className="text-gray-600">Adults</span>
          <input
            type="number"
            value={adults}
            min={1}
            max={6}
            onChange={(e) => setAdults(parseInt(e.target.value, 10) || 1)}
            className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm"
          />
        </label>
        <label className="block text-sm">
          <span className="text-gray-600">Guest first name</span>
          <input
            type="text"
            value={guestFirstName}
            onChange={(e) => setGuestFirstName(e.target.value)}
            className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm"
          />
        </label>
        <label className="block text-sm">
          <span className="text-gray-600">Guest last name</span>
          <input
            type="text"
            value={guestLastName}
            onChange={(e) => setGuestLastName(e.target.value)}
            className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm"
          />
        </label>
        <label className="block text-sm md:col-span-2">
          <span className="text-gray-600">Guest email</span>
          <input
            type="email"
            value={guestEmail}
            onChange={(e) => setGuestEmail(e.target.value)}
            className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm"
          />
        </label>
      </div>

      <div className="mt-4 flex gap-2">
        <button
          type="button"
          onClick={loadRate}
          disabled={rateLoading}
          className="rounded bg-gray-800 px-4 py-2 text-sm font-medium text-white hover:bg-gray-700 disabled:opacity-50"
        >
          {rateLoading ? 'Loading…' : '1. Look up rate'}
        </button>
        <button
          type="button"
          onClick={() => setModalOpen(true)}
          disabled={!rate}
          className="rounded bg-catch-red px-4 py-2 text-sm font-medium text-white hover:bg-catch-red/90 disabled:opacity-50"
        >
          2. Open booking modal
        </button>
      </div>

      {rateError && (
        <p className="mt-3 text-sm text-red-600">{rateError}</p>
      )}

      {rate && (
        <div className="mt-4 rounded border border-gray-200 bg-gray-50 p-3 text-sm">
          <p className="font-medium">{rate.room_name} ({rate.board_name ?? 'Room only'})</p>
          <p className="mt-1 text-lg font-semibold">
            ${rate.price.toFixed(2)} {rate.currency}
          </p>
          {rate.free_cancel_until && (
            <p className="mt-1 text-xs text-gray-600">
              Free cancellation until {rate.free_cancel_until}
            </p>
          )}
        </div>
      )}

      {modalOpen && rate && (
        <HotelBookingModal
          rate={rate}
          hotelName={hotelName}
          returnUrlBase={`${window.location.origin}/book-hotel`}
          apiBaseUrl={API_BASE_URL}
          authToken={localStorage.getItem('access_token') ?? ''}
          bookingContext={bookingContext}
          onClose={() => setModalOpen(false)}
        />
      )}
    </div>
  )
}
